package com.spground.spaceplaygroundwallpaper

import kotlinx.browser.document
import org.w3c.dom.CanvasImageSource
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.Path2D

/**
 * Minimal android.graphics-shaped shim over HTML Canvas2D. Only the surface the
 * engine actually touches is implemented, with the same names and semantics, so
 * SpaceSceneEngine stays a near-verbatim copy of the Android source and future
 * syncs are a mechanical diff.
 */

object Color {
    const val BLACK: Int = 0xFF000000.toInt()
    const val WHITE: Int = 0xFFFFFFFF.toInt()
    const val GREEN: Int = 0xFF00FF00.toInt()

    fun rgb(r: Int, g: Int, b: Int): Int = argb(255, r, g, b)
    fun argb(a: Int, r: Int, g: Int, b: Int): Int =
        (a shl 24) or (r shl 16) or (g shl 8) or b

    fun alpha(color: Int): Int = color ushr 24
    fun red(color: Int): Int = (color shr 16) and 0xFF
    fun green(color: Int): Int = (color shr 8) and 0xFF
    fun blue(color: Int): Int = color and 0xFF
}

// CSS color strings are built thousands of times per frame; cache per ARGB int.
private val cssCache = HashMap<Int, String>()
internal fun cssColor(color: Int): String = cssCache.getOrPut(color) {
    val a = Color.alpha(color) / 255.0
    "rgba(${Color.red(color)},${Color.green(color)},${Color.blue(color)},$a)"
}

object PorterDuff {
    enum class Mode { SRC_IN, SRC_ATOP }
}

class PorterDuffColorFilter(val color: Int, val mode: PorterDuff.Mode)

class DashPathEffect(val intervals: FloatArray, val phase: Float)

object Shader {
    enum class TileMode { CLAMP }
}

class LinearGradient(
    val x0: Float, val y0: Float, val x1: Float, val y1: Float,
    val colors: IntArray, val positions: FloatArray,
    @Suppress("UNUSED_PARAMETER") tile: Shader.TileMode,
)

class Paint(@Suppress("UNUSED_PARAMETER") flags: Int = 0) {
    companion object {
        const val ANTI_ALIAS_FLAG = 1
    }

    enum class Style { FILL, STROKE }

    var style: Style = Style.FILL
    var color: Int = Color.BLACK
    var strokeWidth: Float = 1f
    var pathEffect: DashPathEffect? = null
    var shader: LinearGradient? = null
    var colorFilter: PorterDuffColorFilter? = null

    // Matches Android: alpha is the color's alpha byte; bitmap draws use it as
    // the layer alpha while shape draws take alpha from the color itself.
    var alpha: Int
        get() = Color.alpha(color)
        set(value) {
            color = (color and 0x00FFFFFF) or (value.coerceIn(0, 255) shl 24)
        }
}

class Path {
    internal var p = Path2D()

    fun reset() { p = Path2D() }
    fun moveTo(x: Float, y: Float) = p.moveTo(x.toDouble(), y.toDouble())
    fun lineTo(x: Float, y: Float) = p.lineTo(x.toDouble(), y.toDouble())
    fun quadTo(cx: Float, cy: Float, x: Float, y: Float) =
        p.quadraticCurveTo(cx.toDouble(), cy.toDouble(), x.toDouble(), y.toDouble())
    fun close() = p.closePath()
}

// Only setScale + postTranslate are used, so a scale/translate pair suffices.
class Matrix {
    var sx = 1f; var sy = 1f; var tx = 0f; var ty = 0f

    fun reset() { sx = 1f; sy = 1f; tx = 0f; ty = 0f }
    fun setScale(x: Float, y: Float) { sx = x; sy = y; tx = 0f; ty = 0f }
    fun postTranslate(x: Float, y: Float) { tx += x; ty += y }
}

class Bitmap internal constructor(
    internal val source: CanvasImageSource,
    val width: Int,
    val height: Int,
    internal val backingCanvas: HTMLCanvasElement? = null,
) {
    enum class Config { ARGB_8888 }

    // Tinted variants keyed by "color/mode". Warp rebuilds star tints with a
    // fresh color every frame, so cap the cache instead of letting it grow.
    internal val tintCache = HashMap<String, HTMLCanvasElement>()

    companion object {
        fun createBitmap(w: Int, h: Int, @Suppress("UNUSED_PARAMETER") config: Config): Bitmap {
            val c = document.createElement("canvas") as HTMLCanvasElement
            c.width = w
            c.height = h
            return Bitmap(c, w, h, backingCanvas = c)
        }

        fun fromImage(source: CanvasImageSource, w: Int, h: Int): Bitmap = Bitmap(source, w, h)
    }
}

class Canvas {
    private var ctx: CanvasRenderingContext2D
    private val pixelW: Int
    private val pixelH: Int

    constructor(ctx: CanvasRenderingContext2D, pixelW: Int, pixelH: Int) {
        this.ctx = ctx
        this.pixelW = pixelW
        this.pixelH = pixelH
    }

    constructor(bitmap: Bitmap) {
        val backing = bitmap.backingCanvas
            ?: error("Canvas(bitmap) requires a createBitmap()-backed bitmap")
        ctx = backing.getContext("2d") as CanvasRenderingContext2D
        pixelW = bitmap.width
        pixelH = bitmap.height
    }

    // Save stack: null = plain ctx.save(); non-null = an offscreen dim layer.
    private class Layer(
        val parent: CanvasRenderingContext2D,
        val canvas: HTMLCanvasElement,
        val alpha: Int,
    )

    private val stack = mutableListOf<Layer?>()

    // The dim layer runs every frame at the default settings, so reuse one
    // offscreen canvas instead of allocating per frame.
    private val layerPool = mutableListOf<HTMLCanvasElement>()

    fun save() {
        stack.add(null)
        ctx.save()
    }

    fun restore() {
        val entry = stack.removeLast()
        if (entry == null) {
            ctx.restore()
            return
        }
        // End of a saveLayerAlpha scope: drop the layer's clip/transform state,
        // then composite it onto the parent at the layer alpha in raw pixels.
        ctx.restore()
        val parent = entry.parent
        parent.save()
        parent.setTransform(1.0, 0.0, 0.0, 1.0, 0.0, 0.0)
        parent.globalAlpha = entry.alpha / 255.0
        parent.drawImage(entry.canvas, 0.0, 0.0)
        parent.restore()
        layerPool.add(entry.canvas)
        ctx = parent
    }

    fun saveLayerAlpha(left: Float, top: Float, right: Float, bottom: Float, alpha: Int) {
        val layerCanvas = layerPool.removeLastOrNull()?.takeIf {
            it.width == pixelW && it.height == pixelH
        } ?: (document.createElement("canvas") as HTMLCanvasElement).also {
            it.width = pixelW
            it.height = pixelH
        }
        val lctx = layerCanvas.getContext("2d") as CanvasRenderingContext2D
        lctx.setTransform(1.0, 0.0, 0.0, 1.0, 0.0, 0.0)
        lctx.clearRect(0.0, 0.0, pixelW.toDouble(), pixelH.toDouble())
        lctx.save()
        // Carry the current transform (parallax translate etc.) into the layer.
        lctx.asDynamic().setTransform(ctx.asDynamic().getTransform())
        lctx.beginPath()
        lctx.rect(left.toDouble(), top.toDouble(), (right - left).toDouble(), (bottom - top).toDouble())
        lctx.clip()
        stack.add(Layer(ctx, layerCanvas, alpha))
        ctx = lctx
    }

    fun translate(x: Float, y: Float) = ctx.translate(x.toDouble(), y.toDouble())
    fun rotate(degrees: Float) = ctx.rotate(degrees * kotlin.math.PI / 180.0)
    fun scale(sx: Float, sy: Float) = ctx.scale(sx.toDouble(), sy.toDouble())

    /** Fills the whole surface regardless of the current transform, like Android. */
    fun drawColor(color: Int) {
        ctx.save()
        ctx.setTransform(1.0, 0.0, 0.0, 1.0, 0.0, 0.0)
        ctx.fillStyle = cssColor(color)
        ctx.fillRect(0.0, 0.0, pixelW.toDouble(), pixelH.toDouble())
        ctx.restore()
    }

    fun drawCircle(cx: Float, cy: Float, r: Float, paint: Paint) {
        if (r <= 0f) return
        ctx.beginPath()
        ctx.arc(cx.toDouble(), cy.toDouble(), r.toDouble(), 0.0, 2.0 * kotlin.math.PI)
        if (paint.style == Paint.Style.STROKE) strokeWith(paint) else fillWith(paint)
    }

    fun drawRect(left: Float, top: Float, right: Float, bottom: Float, paint: Paint) {
        val shader = paint.shader
        ctx.fillStyle = if (shader != null) makeGradient(shader) else cssColor(paint.color)
        ctx.fillRect(left.toDouble(), top.toDouble(), (right - left).toDouble(), (bottom - top).toDouble())
    }

    fun drawLine(x1: Float, y1: Float, x2: Float, y2: Float, paint: Paint) {
        ctx.beginPath()
        ctx.moveTo(x1.toDouble(), y1.toDouble())
        ctx.lineTo(x2.toDouble(), y2.toDouble())
        strokeWith(paint)
    }

    fun drawPath(path: Path, paint: Paint) {
        ctx.fillStyle = cssColor(paint.color)
        ctx.fill(path.p)
    }

    fun drawBitmap(bitmap: Bitmap, left: Float, top: Float, paint: Paint) {
        val src = resolveSource(bitmap, paint.colorFilter)
        ctx.globalAlpha = paint.alpha / 255.0
        ctx.drawImage(src, left.toDouble(), top.toDouble())
        ctx.globalAlpha = 1.0
    }

    fun drawBitmap(bitmap: Bitmap, matrix: Matrix, paint: Paint) {
        val src = resolveSource(bitmap, paint.colorFilter)
        ctx.save()
        ctx.transform(matrix.sx.toDouble(), 0.0, 0.0, matrix.sy.toDouble(), matrix.tx.toDouble(), matrix.ty.toDouble())
        ctx.globalAlpha = paint.alpha / 255.0
        ctx.drawImage(src, 0.0, 0.0)
        ctx.restore()
    }

    private fun fillWith(paint: Paint) {
        ctx.fillStyle = cssColor(paint.color)
        ctx.fill()
    }

    private fun strokeWith(paint: Paint) {
        ctx.strokeStyle = cssColor(paint.color)
        ctx.lineWidth = paint.strokeWidth.toDouble()
        val dash = paint.pathEffect
        if (dash != null) {
            ctx.setLineDash(dash.intervals.map { it.toDouble() }.toTypedArray())
            ctx.lineDashOffset = dash.phase.toDouble()
        } else {
            ctx.setLineDash(emptyArray())
        }
        ctx.stroke()
    }

    private fun makeGradient(shader: LinearGradient): dynamic {
        val g = ctx.createLinearGradient(
            shader.x0.toDouble(), shader.y0.toDouble(),
            shader.x1.toDouble(), shader.y1.toDouble()
        )
        for (i in shader.colors.indices) {
            g.addColorStop(shader.positions[i].toDouble(), cssColor(shader.colors[i]))
        }
        return g
    }

    /**
     * PorterDuffColorFilter emulation: pre-tint the bitmap on an offscreen
     * canvas (SRC_IN = "source-in", SRC_ATOP = "source-atop") and cache it.
     */
    private fun resolveSource(bitmap: Bitmap, filter: PorterDuffColorFilter?): CanvasImageSource {
        if (filter == null) return bitmap.source
        val key = "${filter.color}/${filter.mode.ordinal}"
        bitmap.tintCache[key]?.let { return it }
        if (bitmap.tintCache.size > 48) bitmap.tintCache.clear()
        val c = document.createElement("canvas") as HTMLCanvasElement
        c.width = bitmap.width
        c.height = bitmap.height
        val tctx = c.getContext("2d") as CanvasRenderingContext2D
        tctx.drawImage(bitmap.source, 0.0, 0.0)
        tctx.globalCompositeOperation = when (filter.mode) {
            PorterDuff.Mode.SRC_IN -> "source-in"
            PorterDuff.Mode.SRC_ATOP -> "source-atop"
        }
        tctx.fillStyle = cssColor(filter.color)
        tctx.fillRect(0.0, 0.0, bitmap.width.toDouble(), bitmap.height.toDouble())
        bitmap.tintCache[key] = c
        return c
    }
}
